import './icon-picker.scss';
import {useEffect, useRef, useState} from 'react';
import {
  Button,
  ButtonBase,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {translations} from '../../i18n/translations';
import {IconsCatalog} from '../../backend/enums/icons-catalog';
import {IconInfo} from '../../backend/enums/icon-info';
import {AppButtonColors} from '../../utils/constants/colors';
import {useTheme} from '@mui/material/styles';
import {LoadingContent} from '../loading-content/loading-content';
import {IconsRepository} from '../../repositories/icons';
import {avatarSizesAndCoefficients} from '../../utils/constants/avatar-sizes-and-coefficients';
import {isSvg} from '../../utils/functions/validate-uri-svg';

/**
 * Icon picker component.
 *
 * It's currently used for the entity services.
 */
export const IconPicker = (props: {
  catalog: IconsCatalog;
  appButtonColors: AppButtonColors;
  open: boolean;
  iconUrl: string;
  onClose: (iconUrl?: string) => void;
}) => {
  // Hold ref
  const descriptionElementRef = useRef();
  const [icons, setIcons] = useState<Array<IconInfo>>(new Array<IconInfo>());
  const [currentIconUrl, setCurrentIconUrl] = useState<string>(props.iconUrl);

  // Translations
  const {t} = useTranslation();

  // Get theme
  const theme = useTheme();

  // Pick an icon
  const handlePickIcon = (newSelectedIconUrl: string) => {
    // Update selection
    setCurrentIconUrl(newSelectedIconUrl);
  };

  // Close modal
  const handleClose = (confirmed: boolean) => {
    // Callback with previous value
    if (confirmed) {
      // Yes
      props.onClose(currentIconUrl);
    } else {
      // Cancelled
      props.onClose(props.iconUrl);
    }
  };

  // On mount
  useEffect(() => {
    const bootstrap = async () => {
      // Lazy Load icons
      if (!icons || icons.length === 0) {
        // Get icons
        const result: Array<IconInfo> = await IconsRepository.instance.getByCatalog(props.catalog);

        // Done
        setIcons(result);
      }
    };

    // If modal should open
    if (props.open) {
      // Get element ref
      const {current: descriptionElement} = descriptionElementRef;

      // If element exists
      if (descriptionElement) {
        // Give the focus to it
        (descriptionElement as any).focus();
      }
    }

    // bootstrap
    bootstrap();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  return (
    <Dialog open={props.open} scroll="paper">
      <DialogTitle id="icon-picker-scroll-dialog-title">
        {t(translations.icons_picker.title)}
      </DialogTitle>
      <DialogContent dividers={true}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            margin: '-8px',
          }}
          ref={descriptionElementRef}
          tabIndex={-1}
        >
          {icons && icons.length > 0 ? (
            <>
              {icons.map((value: IconInfo) => (
                <Box
                  key={`icon-picker-${value.name}`}
                  sx={{
                    margin: '6px',
                  }}
                >
                  <ButtonBase
                    sx={{borderRadius: '50%'}}
                    onClick={() => {
                      handlePickIcon(value.url);
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: props.appButtonColors.enabled,
                        borderWidth: currentIconUrl === value.url ? '2px' : 0,
                        borderColor: theme.palette.primary.main,
                        borderStyle: 'solid',
                        borderRadius: `${
                          avatarSizesAndCoefficients.avatarSizes.extraGiant *
                          avatarSizesAndCoefficients.borderRadiusCoefficients.circle
                        }px`,
                        width: `${avatarSizesAndCoefficients.avatarSizes.extraGiant}px`,
                        height: `${avatarSizesAndCoefficients.avatarSizes.extraGiant}px`,
                      }}
                    >
                      {isSvg(value.url) ? (
                        <object
                          data={value.url}
                          aria-label={value.name}
                          type="image/svg+xml"
                          width={`${
                            avatarSizesAndCoefficients.avatarSizes.extraGiant *
                            avatarSizesAndCoefficients.iconSizeCoefficient
                          }`}
                          height={`${
                            avatarSizesAndCoefficients.avatarSizes.extraGiant *
                            avatarSizesAndCoefficients.iconSizeCoefficient
                          }`}
                          className="icon-picker-svg-object"
                        ></object>
                      ) : (
                        <img
                          width={`${
                            avatarSizesAndCoefficients.avatarSizes.extraGiant *
                            avatarSizesAndCoefficients.iconSizeCoefficient
                          }px`}
                          height={`${
                            avatarSizesAndCoefficients.avatarSizes.extraGiant *
                            avatarSizesAndCoefficients.iconSizeCoefficient
                          }px`}
                          src={value.url}
                          alt={value.name}
                        />
                      )}
                    </Box>
                  </ButtonBase>
                </Box>
              ))}
            </>
          ) : (
            <Box sx={{width: '100%'}}>
              <LoadingContent />
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{justifyContent: 'center', gap: '10px', padding: '12px 22px'}}>
        <Button
          variant="text"
          color="error"
          sx={{width: '100%', maxWidth: '252px'}}
          onClick={() => {
            handleClose(false);
          }}
        >
          {t(translations.icons_picker.cancel_button)}
        </Button>
        <Button
          variant="contained"
          color="primary"
          sx={{width: '100%', maxWidth: '252px'}}
          onClick={() => {
            handleClose(true);
          }}
        >
          {t(translations.icons_picker.confirm_button)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
